import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, Users, BarChart3, LogOut, Check, AlertCircle, Inbox } from "lucide-react";

import LoginForm from "./components/LoginForm";
import PostsTab from "./components/PostsTab";
import SubscribersTab from "./components/SubscribersTab";
import MessagesTab from "./components/MessagesTab";
import UsersTab from "./components/UsersTab";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("posts");
  const [notification, setNotification] = useState(null);

  // Check existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("admin_token");
      if (!token) {
        setCheckingAuth(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem("admin_token");
        }
      } catch (error) {
        console.error("Auth check error:", error);
      }
      setCheckingAuth(false);
    };

    checkAuth();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return {
      Authorization: `Bearer ${token}`
    };
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: "POST",
        headers: getAuthHeaders()
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("admin_token");
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const handleLoginSuccess = (userData) => {
    setCurrentUser(userData);
    setIsAuthenticated(true);
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Loading state
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-500 mt-4">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  // Tab content
  const tabs = [
    { id: "posts", label: "Posts", icon: FileText },
    { id: "subscribers", label: "Suscriptores", icon: BarChart3 },
    { id: "messages", label: "Mensajes", icon: Inbox },
    { id: "users", label: "Usuarios", icon: Users }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "posts":
        return <PostsTab getAuthHeaders={getAuthHeaders} showNotification={showNotification} />;
      case "subscribers":
        return <SubscribersTab getAuthHeaders={getAuthHeaders} />;
      case "messages":
        return <MessagesTab getAuthHeaders={getAuthHeaders} />;
      case "users":
        return <UsersTab getAuthHeaders={getAuthHeaders} showNotification={showNotification} currentUser={currentUser} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
            notification.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
          }`}
        >
          {notification.type === "success" ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {notification.message}
        </motion.div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Panel de Administración</h1>
              <p className="text-sm text-gray-500">Flowing Blog Manager</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">{currentUser?.display_name}</div>
              <div className="text-xs text-gray-500 capitalize">{currentUser?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Salir
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1">
            {tabs.map((tab) => {
              // Hide users tab for non-admins
              if (tab.id === "users" && currentUser?.role !== "admin") return null;
              
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? "text-orange-500 border-orange-500"
                      : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {renderTabContent()}
      </main>
    </div>
  );
};

export default AdminPanel;
